import React, { useState, useEffect } from "react";
import { Eye, EyeOff, Camera, User } from "lucide-react";
import axios from "axios";
import { getAccessToken } from "../storage/storage";
import Navbar from "./shared/Navbar";

const EditProfile = () => {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [showPasswordHints, setShowPasswordHints] = useState(false);
  const [loading, setLoading] = useState(true);
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const token = getAccessToken();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    currentPassword: "",
    newPassword: "",
  });

  const [initialData, setInitialData] = useState(formData);
  const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialData);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = getAccessToken();
        const res = await axios.get(`${BASE_URL}/api/profile/`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setFormData((prev) => ({
          ...prev,
          firstName: res.data.first_name || "",
          lastName: res.data.last_name || "",
          email: res.data.email || "",
          phone: res.data.phone || "",
        }));

        if (res.data.profile_picture) {
          setProfileImage(res.data.profile_picture);
        }

        setInitialData({
          firstName: res.data.first_name || "",
          lastName: res.data.last_name || "",
          email: res.data.email || "",
          phone: res.data.phone || "",
          currentPassword: "",
          newPassword: "",
        });
      } catch (err) {
        console.error("Error fetching profile:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => setProfileImage(e.target?.result);
      reader.readAsDataURL(file);
    }
  };

  const handlePasswordChange = async () => {
    try {
      const res = await axios.put(
        `${BASE_URL}/api/change/password/`,
        {
          old_password: formData.currentPassword,
          new_password: formData.newPassword,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log("Password updated:", res.data);
      alert(res.data.detail || "Password updated successfully");

      setFormData({
        ...formData,
        currentPassword: "",
        newPassword: "",
      });
    } catch (err) {
      console.error("Error updating password:", err);
      alert("Failed to update password");
    }
  };

  const handleCancel = () => {
    setFormData(initialData);
    setProfileImage(null);
  };

  const handleSaveChanges = async (e) => {
    e.preventDefault();
    try {
      const token = getAccessToken();
      await axios.put(
        `${BASE_URL}/api/profile/update/`,
        {
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone: formData.phone,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert("Profile updated successfully");

      setInitialData({
        ...formData,
        currentPassword: "",
        newPassword: "",
      });
    } catch (err) {
      console.error("Error saving profile:", err);
      alert(err.response?.data?.detail || "Failed to update profile");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-gray-600">
        Loading profile...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-0 pb-8 px-4 sm:px-6 lg:px-8">
      <Navbar />
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mt-5 text-gray-900">
            User Settings
          </h1>
          <p className="mt-2 text-gray-600">Manage your account details</p>
        </div>

        <form onSubmit={handleSaveChanges} className="space-y-8">
          {/* Profile Picture Section */}
          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Profile Picture
            </h2>
            <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6">
              <div className="relative">
                <div className="w-32 h-32 rounded-full border-4 border-white shadow-lg overflow-hidden bg-gray-100">
                  {profileImage ? (
                    <img
                      src={profileImage}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-green-100">
                      <User className="w-12 h-12 text-[#175635]" />
                    </div>
                  )}
                </div>
                <label className="absolute bottom-0 right-0 bg-[#175635] hover:bg-green-700 text-white p-2 rounded-full cursor-pointer transition-colors duration-200 shadow-lg">
                  <Camera className="w-4 h-4" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </label>
              </div>
              <div className="text-center sm:text-left">
                <h3 className="text-lg font-medium text-gray-900">
                  {formData.firstName} {formData.lastName}
                </h3>
                <p className="text-gray-500 mb-3">{formData.email}</p>
                <p className="text-sm text-gray-400">
                  Upload a new profile picture. Recommended size: 400x400px
                </p>
              </div>
            </div>
          </div>

          {/* Personal Information */}
          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Personal Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  First Name
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#175635]"
                  placeholder="Enter your first name"
                />
              </div>
              <div>
                <label
                  htmlFor="lastName"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Last Name
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#175635]"
                  placeholder="Enter your last name"
                />
              </div>
              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Phone Number
                </label>
                <input
                  type="text"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#175635]"
                  placeholder="Enter your phone number"
                />
              </div>

              <div className="md:col-span-2">
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  disabled
                  className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100 cursor-not-allowed opacity-75"
                  placeholder="your.email@example.com"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Email cannot be changed. Contact support if you need to update
                  it.
                </p>
              </div>
            </div>
          </div>

          {/* Password Update Section */}
          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Update Password
            </h2>

            <div className="space-y-6">
              <div>
                <label
                  htmlFor="currentPassword"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    id="currentPassword"
                    name="currentPassword"
                    value={formData.currentPassword}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 pr-12 focus:outline-none focus:ring-2 focus:ring-[#175635]"
                    placeholder="Enter your current password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  >
                    {showCurrentPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label
                  htmlFor="newPassword"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    id="newPassword"
                    name="newPassword"
                    value={formData.newPassword}
                    onFocus={() => setShowPasswordHints(true)}
                    onBlur={() => {
                      if (!formData.newPassword) {
                        setShowPasswordHints(false);
                      }
                    }}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 pr-12 focus:outline-none focus:ring-2 focus:ring-[#175635]"
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  >
                    {showNewPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* {showPasswordHints && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-[#175635] mb-2">
                    Password Requirements:
                  </h4>
                  <ul className="text-xs text-gray-600 space-y-1">
                    <li>• At least 8 characters long</li>
                    <li>• Include uppercase and lowercase letters</li>
                    <li>• Include at least one number</li>
                    <li>• Include at least one special character</li>
                  </ul>
                </div>
              )} */}

              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={handlePasswordChange}
                  className="px-8 py-3 rounded-lg font-semibold shadow bg-[#175635] text-white hover:bg-green-700 transition"
                >
                  Update Password
                </button>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center gap-4">
            <button
              type="button"
              onClick={handleCancel}
              className="px-8 py-3 rounded-lg font-semibold shadow bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!hasChanges}
              className={`px-8 py-3 rounded-lg font-semibold shadow transition ${
                hasChanges
                  ? "bg-[#175635] text-white hover:bg-green-700"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfile;
